/**
 * Everything below the router: providers, the onboarding gate, and the shell.
 *
 * Split from `App` so a test can supply its own router and its own ports
 * without the app's `BrowserRouter` and browser adapters being constructed
 * first. `App` is then two lines and has nothing to test.
 */

import type { ReactNode } from "react";
import { useEffect } from "react";
import { useNavigate } from "react-router";
import { LayoutScope } from "@ff/ui";
import type { ShellActivity } from "../app-state/activity";
import {
  PassphraseChallengeSurface,
  useDeviceVerification,
} from "../app-state/deviceVerification";
import { HistoryScope } from "../app-state/historyHost";
import { JournalScope } from "../app-state/journalHost";
import { LiveScope } from "../app-state/liveHost";
import { useNavigation } from "../app-state/navigation";
import type { PortBundle } from "../app-state/ports";
import { AppStateProvider } from "../app-state/providers";
import { OnboardingFlow } from "../features/onboarding/OnboardingFlow";
import { useOnboardingGate } from "../features/onboarding/state";
import { AppShell } from "./AppShell";
import type { DestinationId } from "./destinations";
import { destinationFor } from "./destinations";

/**
 * Writes the URL from the shell's state, and never the other way round.
 *
 * F-NAV-23: no deep links, no restoration, and a cold launch always on Train.
 * Because the effect runs on mount, whatever address the document was opened
 * at is replaced by Train's path before anything can read it — which is the
 * whole enforcement mechanism, not a side effect of one. `replace` rather than
 * `push` so the browser's Back button leaves the app instead of walking a
 * history of tab switches the native product never had.
 */
function UrlMirror(): null {
  const navigate = useNavigate();
  const { destination, fullViewportPath } = useNavigation();

  useEffect(() => {
    navigate(fullViewportPath ?? destinationFor(destination).path, { replace: true });
  }, [navigate, destination, fullViewportPath]);

  return null;
}

function ShellOrOnboarding(): ReactNode {
  const onboarding = useOnboardingGate();
  // F-NAV-16: onboarding replaces the whole shell. The four roots are not
  // mounted behind it — there is nothing yet for them to preserve, and mounting
  // them would start a workout-data refresh before the user has agreed to be
  // here.
  if (!onboarding.completed) return <OnboardingFlow onComplete={onboarding.complete} />;
  return <AppShell />;
}

export interface AppRootProps {
  readonly ports: PortBundle;
  readonly activity?: ShellActivity;
  readonly rootElement?: HTMLElement | null;
  readonly shortcutTarget?: EventTarget | null;
  readonly initialDestination?: DestinationId;
}

/**
 * The whole tree below the router.
 *
 * One scope wraps the shell. `JournalScope` carries the shared privacy session,
 * the durable repository and every Journal provider. It sits *above* the shell rather than inside the
 * Journal destination root because three of the surfaces it serves are not in
 * that root: the composer, the reader and the goals workspace are full-viewport
 * routes the shell mounts beside the roots, Settings owns the Journal privacy
 * pane, and Train's corner note reads the same session. Mounting it in the
 * Journal root would give each of those either nothing or a second session.
 *
 * The verification port is filled in here rather than in `browserPorts.ts`
 * because its passphrase enrolment cannot answer without a rendered field, and
 * the field is {@link PassphraseChallengeSurface} — mounted in the same tree, in
 * the same commit, so a `verify()` can always be answered.
 */
export function AppRoot({
  ports,
  activity,
  rootElement,
  shortcutTarget,
  initialDestination,
}: AppRootProps): ReactNode {
  const verification = useDeviceVerification(ports);

  return (
    <AppStateProvider
      ports={verification.ports}
      activity={activity}
      rootElement={rootElement}
      shortcutTarget={shortcutTarget}
      initialDestination={initialDestination}
    >
      {/*
        No measurement override. The scope measures itself exactly as it does in
        the app; a test pins the *environment* with the layout shim instead, so a
        surface that forgot to measure cannot pass by being handed a width.
      */}
      <JournalScope
        {...(verification.dropKeyMaterial === undefined
          ? {}
          : { dropKeyMaterial: verification.dropKeyMaterial })}
      >
        <LayoutScope data-testid="app.scope">
          <UrlMirror />
          {/*
            Above the shell for the same reason `JournalScope` is: the surfaces
            it serves are full-viewport routes `AppShell` mounts beside the four
            destination roots, so a provider inside a root cannot be seen by
            them. Inside `JournalScope` because both capabilities it supplies —
            the repository F-HS-20 asks, and the composer session F-HS-12 opens
            — are the Journal's shared ones.
          */}
          <HistoryScope>
            {/*
              Above the shell for the third time, and for the most concrete
              reason of the three: `AppShell` mounts `/train/live` — the surface
              F-NAV-08's Ringside action and Build's Start both open — beside
              the four destination roots, so the one live session has to be in
              scope for the shell itself. A host inside the Build root could
              only ever be a second workout, which is what this replaced.
            */}
            <LiveScope>
              <ShellOrOnboarding />
            </LiveScope>
          </HistoryScope>
          {/*
            Last, so the challenge paints over whatever raised it — the
            Journal curtain, Settings' lock toggle, or Train's corner note.
          */}
          <PassphraseChallengeSurface broker={verification.challenges} />
        </LayoutScope>
      </JournalScope>
    </AppStateProvider>
  );
}
